import { useState, useEffect } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MessageSquare, Trash2, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function CommentsDialog({ profile, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/profiles/${profile.id}/comments`);
      setComments(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && profile) load();
  }, [open, profile]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/profiles/${profile.id}/comments`, { text });
      setText("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error("Não foi possível enviar o comentário");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error("Não foi possível eliminar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-lg" data-testid="comments-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-normal flex items-center gap-3">
            <img src={imgProxy(profile?.photo_url, 160)} alt="" loading="lazy" decoding="async" className="w-9 h-9 object-cover grayscale-[20%] bg-[#27272a]" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
            <div>
              <div className="text-base">{profile?.name}</div>
              <div className="text-xs text-[#71717a]">{profile?.source_name} · {profile?.city || "—"}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
          {loading && <div className="text-sm text-[#71717a]">A carregar…</div>}
          {!loading && comments.length === 0 && (
            <div className="text-sm text-[#71717a] py-6 text-center">Ainda não há comentários. Sê o primeiro.</div>
          )}
          {comments.map((c) => (
            <div key={c.id} className="border border-[#27272a] bg-[#09090b] p-3" data-testid="comment-item">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#c8aa6e]">{c.author_name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#71717a]">{timeAgo(c.created_at)}</span>
                  {(c.author_email === user?.email || user?.role === "admin") && (
                    <button onClick={() => remove(c.id)} className="text-[#71717a] hover:text-[#ef4444]" data-testid="comment-delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-[#e4e4e7] whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="flex items-end gap-2 pt-2 border-t border-[#27272a]">
          <textarea
            data-testid="comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="Escreve uma nota sobre este perfil…"
            className="flex-1 bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm resize-none"
          />
          <button
            data-testid="comment-submit"
            type="submit"
            disabled={sending || !text.trim()}
            className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-2.5 transition-colors disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { MessageSquare };
